import { useMemo } from 'react';
import { useAvContext } from '../../../context/AvContextProvider';
import { useIsFreaProvisioned } from '../../../FREA/views/FREASettings/hooks';
import { AppPathsValue, PathFeatureFlag, Permission } from '../../../types';
import { MenuItem, NavRequirement, SubMenuItem } from '../../../types/sideBar.types';
import { isVisibleByFeatureFlags } from '../navigation.utils';
import { appsSideBar } from '../sidebar.constants';

/**
 * `appsSideBar` reduced to the items the current user may see: back-office, role, permission and
 * feature-flag guards, plus runtime `requires` conditions. Parents left without visible children are dropped.
 * Nested items are checked with their parent's `permission`.
 */
const useVisibleAppsSideBar = (): Record<string, MenuItem[]> => {
  const {
    featureFlags,
    userPermissions: { hasAllowedPermission, isAvalorInternalRole, isBackOfficeAvailable },
  } = useAvContext();

  const passesGuards = (item: SubMenuItem, app: string, permission = item.permission) =>
    (!item.isBackOffice || isBackOfficeAvailable) &&
    (!PathFeatureFlag[item.path] || !!featureFlags[PathFeatureFlag[item.path]]) &&
    isVisibleByFeatureFlags(item, featureFlags) &&
    (!!item.subMenu ||
      (item.internalRoleOnly
        ? isAvalorInternalRole
        : !!hasAllowedPermission({ path: item.permissionPath ?? item.path, app: app as AppPathsValue, permission })));

  const hasGuardedItemRequiring = (items: SubMenuItem[], app: string, requirement: NavRequirement): boolean =>
    items.some(
      item =>
        (item.requires === requirement && passesGuards(item, app)) ||
        (!!item.subMenu && hasGuardedItemRequiring(item.subMenu, app, requirement))
    );

  const isRequired = (requirement: NavRequirement) =>
    Object.entries(appsSideBar).some(([app, menus]) => menus.some(menu => hasGuardedItemRequiring(menu.subMenu, app, requirement)));

  const isFreaProvisioned = useIsFreaProvisioned(isRequired(NavRequirement.FreaProvisioned));

  return useMemo(() => {
    const requirements: Record<NavRequirement, boolean> = {
      [NavRequirement.FreaProvisioned]: isFreaProvisioned,
    };

    const filterItems = (items: SubMenuItem[], app: string, parentPermission?: Permission): SubMenuItem[] =>
      items.flatMap(item => {
        if (!passesGuards(item, app, parentPermission ?? item.permission) || (item.requires && !requirements[item.requires])) {
          return [];
        }
        if (!item.subMenu) {
          return [item];
        }
        const subMenu = filterItems(item.subMenu, app, item.permission);
        return subMenu.length ? [{ ...item, subMenu }] : [];
      });

    return Object.fromEntries(
      Object.entries(appsSideBar).map(([app, menus]) => [
        app,
        menus
          .filter(menu => isVisibleByFeatureFlags(menu, featureFlags))
          .map(menu => ({ ...menu, subMenu: filterItems(menu.subMenu, app) })),
      ])
    );
  }, [featureFlags, hasAllowedPermission, isAvalorInternalRole, isBackOfficeAvailable, isFreaProvisioned]);
};

export default useVisibleAppsSideBar;
